import React, { useState, useEffect, useMemo } from 'react';
import { FlightAnalysis } from '../types';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatPrice } from '../utils/currency';
import './DetailedBreakdown.css';

interface DetailedBreakdownProps {
  data: FlightAnalysis;
}

interface CostItem {
  key: string;
  label: string;
  value: number;
  required: boolean;
  isRisk?: boolean;
}

const DetailedBreakdown: React.FC<DetailedBreakdownProps> = ({ data }) => {
  const { breakdown } = data;
  const { currency, currencyInfo } = useCurrency();
  const displayCurrency = data.currency || currency;

  const allCostItems: CostItem[] = useMemo(() => [
    { key: 'basePrice', label: 'Base Price', value: breakdown.basePrice, required: true },
    { key: 'checkedBaggage', label: 'Checked Baggage', value: breakdown.checkedBaggage || 0, required: false },
    { key: 'carryOn', label: 'Carry-on Baggage', value: breakdown.carryOn || 0, required: false },
    { key: 'seatSelection', label: 'Seat Selection', value: breakdown.seatSelection || 0, required: false },
    { key: 'meals', label: 'Meals', value: breakdown.meals || 0, required: false },
    { key: 'publicTransportCost', label: 'Additional Transport Costs', value: breakdown.publicTransportCost || 0, required: false },
  ], [breakdown]);

  // Initialize all optional items as checked
  const [checkedItems, setCheckedItems] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    allCostItems.forEach(item => {
      if (!item.required && item.value > 0) {
        initial.add(item.key);
      }
    });
    return initial;
  });

  // Reset checkboxes when data changes
  useEffect(() => {
    const initial = new Set<string>();
    allCostItems.forEach(item => {
      if (!item.required && item.value > 0) {
        initial.add(item.key);
      }
    });
    setCheckedItems(initial);
  }, [allCostItems]);

  // Calculate total based on checked items
  const currentTotal = useMemo(() => {
    let total = breakdown.basePrice;
    allCostItems.forEach(item => {
      // Skip basePrice since we already added it above
      if (item.key !== 'basePrice' && (item.required || checkedItems.has(item.key))) {
        total += item.value;
      }
    });
    return total;
  }, [breakdown.basePrice, allCostItems, checkedItems]);

  const handleCheckboxChange = (key: string) => {
    setCheckedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const costItems = allCostItems.filter(item => item.value > 0 || item.required);

  return (
    <div className="detailed-breakdown">
      <div className="breakdown-card">
        <p className="currency-indicator">
          All prices in {displayCurrency} {currencyInfo?.symbol ? `(${currencyInfo.symbol})` : ''}
        </p>

        <div className="cost-items">
          {costItems.map((item) => (
            <div key={item.key} className={`cost-item ${item.isRisk ? 'risk-item' : ''}`}>
              <div className="cost-item-label">
                {!item.required && (
                  <input
                    type="checkbox"
                    checked={checkedItems.has(item.key)}
                    onChange={() => handleCheckboxChange(item.key)}
                    className="cost-checkbox"
                  />
                )}
                <span>{item.label}</span>
                {item.isRisk && <span className="risk-badge">Risk</span>}
                {!item.required && <span className="optional-badge">Optional</span>}
              </div>
              <div className="cost-item-value">
                {formatPrice(item.value, displayCurrency)}
              </div>
            </div>
          ))}
        </div>

        <div className="breakdown-total">
          <div className="total-label">Total Estimated Cost</div>
          <div className="total-value">{formatPrice(currentTotal, displayCurrency)}</div>
        </div>
      </div>
    </div>
  );
};

export default DetailedBreakdown;

